import { Link } from 'react-router';
import { useAuth } from '../auth';
import { fmtDate, fmtUsd } from '../api';
import { Badge, Card, Empty, ErrorBox, Loading, PageHeader, StateBadge, useApi } from '../components/ui';

interface Dashboard {
  decisions: { approvalsForMe: number; pendingTotal: number; plansToConfirm: number; attention: { id: string; state: string; reason: string | null }[] };
  operations: Record<string, number | null | boolean | string>;
  channel: { metric: string; label: string; value: number | null; unavailableReason: string | null; source: string }[];
  upcoming: { id: string; channel: string; state: string; scheduledAt: string }[];
  radar: { id: string; title: string; url: string; relevance: number | null; topic: string | null; pautaSuggestion: string | null }[];
  agents: { id: string; name: string; status: string; lastRun: { status: string; at: string } | null }[];
  killSwitch: boolean;
}

function Stat({ label, value, hint, to }: { label: string; value: string | number; hint?: string; to?: string }) {
  const body = (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-brand-900">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function Home() {
  const { me } = useAuth();
  const { data, error, loading } = useApi<Dashboard>('/api/dashboard');
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const o = data.operations;
  return (
    <>
      <PageHeader title="Command Center" subtitle="Decisões pendentes, operação, radar, agenda e estado dos agentes." />
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Aguardando minha aprovação" value={data.decisions.approvalsForMe} hint={`${data.decisions.pendingTotal} no total`} to="/aprovacoes" />
        <Stat label="Plano semanal a confirmar" value={data.decisions.plansToConfirm} to="/cockpit" />
        <Stat label="Demandas na semana" value={String(o.requestsCreated)} hint={`${o.assetsPublished} publicadas`} />
        <Stat label="Custo de IA (semana)" value={fmtUsd(o.aiCostUsd as number)} hint={me?.llm === 'mock' ? 'estimado — provedor simulado' : o.aiCostIsEstimate ? 'Claude real + estimativas do modo simulado' : 'real (Claude)'} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Precisa de atenção" className="lg:col-span-2">
          {data.decisions.attention.length ? (
            <ul className="divide-y divide-slate-100">
              {data.decisions.attention.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <Link className="font-medium text-brand-700 underline" to={`/conteudo/${a.id}`}>
                    {a.id}
                  </Link>
                  <StateBadge state={a.state} />
                  <span className="text-slate-600">{a.reason}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Nada bloqueado.</Empty>
          )}
        </Card>
        <Card title="Agenda (próximos 7 dias)">
          {data.upcoming.length ? (
            <ul className="space-y-2 text-sm">
              {data.upcoming.map((u) => (
                <li key={u.id} className="flex items-center justify-between gap-2">
                  <Link to={`/conteudo/${u.id}`} className="text-brand-700 underline">
                    {u.id}
                  </Link>
                  <span className="text-xs text-slate-600">{fmtDate(u.scheduledAt)}</span>
                  <StateBadge state={u.state} />
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Sem publicações previstas.</Empty>
          )}
        </Card>
        <Card title="Radar — destaques" className="lg:col-span-2" actions={<Link className="text-sm text-brand-700 underline" to="/radar">ver radar</Link>}>
          {data.radar.length ? (
            <ul className="space-y-2 text-sm">
              {data.radar.map((r) => (
                <li key={r.id}>
                  <Badge tone="blue">{r.relevance ?? '—'}</Badge>{' '}
                  <a className="underline" href={r.url} target="_blank" rel="noreferrer noopener">
                    {r.title}
                  </a>{' '}
                  <span className="text-xs text-slate-500">· {r.topic}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Radar ainda não executado. Ele roda em dias úteis às 08:30 ou manualmente.</Empty>
          )}
        </Card>
        <Card title="Métricas de canal">
          <ul className="space-y-2 text-sm">
            {data.channel.map((m) => (
              <li key={m.metric} className="flex justify-between gap-2">
                <span>{m.label}</span>
                {m.value === null ? <Badge title={m.unavailableReason ?? ''}>indisponível</Badge> : <strong>{m.value}</strong>}
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Agentes" className="lg:col-span-3" actions={<Link className="text-sm text-brand-700 underline" to="/agentes">catálogo</Link>}>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-5">
            {data.agents.map((a) => (
              <Link key={a.id} to={`/agentes/${a.id}`} className="rounded border border-slate-200 p-2 text-xs hover:bg-slate-50">
                <div className="font-semibold">
                  {a.id} <StateBadge state={a.status} />
                </div>
                <div className="truncate text-slate-600" title={a.name}>
                  {a.name}
                </div>
                <div className="text-slate-500">{a.lastRun ? `${a.lastRun.status} · ${fmtDate(a.lastRun.at)}` : 'sem execuções'}</div>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
